import React from 'react';
import { UserOutlined, RobotOutlined } from '@ant-design/icons';
import useReveal from '../../AIEmployees/useReveal';
import { trackEvent } from '../../../utils/analytics';
import './A2ATwoInterfaces.css';

/**
 * A2ATwoInterfaces — the core thesis diagram: every software has a human
 * interface and an agent interface; CellCog commits to full parity between them.
 */
const A2ATwoInterfaces = () => {
  const [ref, visible] = useReveal();
  // section_view: fires once, the first time this reveal-gated section enters
  // the viewport — per-section scroll depth for this page (same pattern as
  // /ai-organization).
  const seenRef = React.useRef(false);
  React.useEffect(() => {
    if (visible && !seenRef.current) {
      seenRef.current = true;
      trackEvent('section_view', { section: 'two-interfaces', page: 'agent-to-agent' });
    }
  }, [visible]);
  return (
    <section className="a2a-two aie-section">
      <div className="aie-section-head">
        <h2 className="aie-section-title aie-display">Every software, two interfaces</h2>
        <p className="aie-section-sub">
          One for the human at the keyboard. One for the agent acting on their
          behalf. We&rsquo;re committed to making them equal.
        </p>
      </div>
      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`} ref={ref}>
        <div className="a2a-two__diagram">
          <div className="a2a-two__col">
            <span className="a2a-two__col-tag"><UserOutlined /> For humans</span>
            <h3 className="a2a-two__col-title">The CellCog app</h3>
            <p className="a2a-two__col-body">
              Chat, co-work, dashboards, the visual workspace: the interface a
              person uses to direct a super-agent.
            </p>
          </div>
          <div className="a2a-two__mid">=</div>
          <div className="a2a-two__col is-agent">
            <span className="a2a-two__col-tag"><RobotOutlined /> For agents</span>
            <h3 className="a2a-two__col-title">The CellCog platform</h3>
            <p className="a2a-two__col-body">
              The exact same capabilities (research, every modality, tools)
              exposed to any agent through one API. No feature left human-only.
            </p>
          </div>
        </div>
        <p className="a2a-two__product">
          <strong>Full parity is the commitment.</strong> Anything a human can do
          on CellCog, an agent can do too: programmatically, at scale.
        </p>
      </div>
    </section>
  );
};

export default A2ATwoInterfaces;
